import React from 'react';
import styled from 'styled-components';
import { scenarios, CATEGORY_ORDER } from '../data/scenarios';

const Container = styled.div`
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 860px;
  width: 100%;
`;

const Title = styled.h2`
  color: #0d9488;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Intro = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const CategoryLabel = styled.h3`
  color: #0f766e;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 3.25rem 0 0.85rem;

  &:first-of-type {
    margin-top: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 1rem;
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  gap: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  cursor: pointer;
  background-color: white;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    border-color: #0d9488;
  }
`;

const Emoji = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

const CardBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 0.35rem;
`;

const MediumLabel = styled.span`
  flex-shrink: 0;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888;
`;

// Difficulty, shown as filled dots out of 3: how easy the other person is to
// win over (Easy = receptive, Hard = guarded).
const DIFFICULTY_LEVEL = { Easy: 1, Medium: 2, Hard: 3 };

const Dots = styled.span`
  flex-shrink: 0;
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  white-space: nowrap;
  margin-top: 0.35rem;
`;

const FilledDot = styled.span`
  color: #0d9488;
`;

const EmptyDot = styled.span`
  color: #ddd;
`;

const difficultyDots = (level) =>
  Array.from({ length: 3 }, (_, i) =>
    i < (DIFFICULTY_LEVEL[level] || 0) ? (
      <FilledDot key={i}>●</FilledDot>
    ) : (
      <EmptyDot key={i}>●</EmptyDot>
    )
  );

const CardName = styled.h4`
  margin: 0.35rem 0 0;
  color: #222;
  font-size: 1.05rem;
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #666;
  line-height: 1.4;
`;

// Practice mode. Realistic adds a reply window to in-person scenarios: if you
// go quiet, the other person reacts rather than waiting for you.
const ModeRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const ModeButton = styled.button`
  padding: 0.5rem 1.1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ $active }) => ($active ? '#0d9488' : '#ddd')};
  background-color: ${({ $active }) => ($active ? '#0d9488' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#666')};

  &:hover {
    border-color: #0d9488;
    color: ${({ $active }) => ($active ? 'white' : '#0d9488')};
  }
`;

const ModeHint = styled.p`
  text-align: center;
  font-size: 0.8rem;
  color: #999;
  margin: 0 0 1.75rem;
  min-height: 1.2em;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.8rem;
  color: #888;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

// The single entry screen: browse scenario cards grouped by category and pick
// one to jump straight into the chat.
const ScenarioSelection = ({ onSelectScenario }) => {
  const [realistic, setRealistic] = React.useState(false);

  const byCategory = CATEGORY_ORDER
    .map((category) => ({
      category,
      // Easiest first within each category (stable — keeps authored order per tier).
      items: scenarios
        .filter((s) => s.category === category)
        .sort((a, b) => (DIFFICULTY_LEVEL[a.difficulty] || 0) - (DIFFICULTY_LEVEL[b.difficulty] || 0)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <Container>
      <Title>What would you like to practice?</Title>
      <Intro>Pick a scenario, have the conversation, and get feedback from your coach.</Intro>

      <ModeRow>
        <ModeButton $active={!realistic} onClick={() => setRealistic(false)}>
          Free practice
        </ModeButton>
        <ModeButton $active={realistic} onClick={() => setRealistic(true)}>
          Realistic
        </ModeButton>
      </ModeRow>
      <ModeHint>
        {realistic
          ? 'In-person scenarios run on a hidden clock. Go quiet and the other person reacts.'
          : 'Take as long as you like to think about each reply.'}
      </ModeHint>

      <Legend>
        {['Easy', 'Medium', 'Hard'].map((level) => (
          <LegendItem key={level}>
            <Dots>{difficultyDots(level)}</Dots>
            {level}
          </LegendItem>
        ))}
      </Legend>

      {byCategory.map(({ category, items }) => (
        <React.Fragment key={category}>
          <CategoryLabel>{category}</CategoryLabel>
          <Grid>
            {items.map((scenario) => (
              <Card key={scenario.id} onClick={() => onSelectScenario(scenario, realistic)}>
                <Emoji aria-hidden="true">{scenario.emoji}</Emoji>
                <CardName>{scenario.title}</CardName>
                <CardDesc>{scenario.description}</CardDesc>
                <CardBottom>
                  <MediumLabel>{scenario.medium === 'text' ? 'Text' : 'Call'}</MediumLabel>
                  <Dots
                    title={`${scenario.difficulty} difficulty`}
                    aria-label={`${scenario.difficulty} difficulty`}
                  >
                    {difficultyDots(scenario.difficulty)}
                  </Dots>
                </CardBottom>
              </Card>
            ))}
          </Grid>
        </React.Fragment>
      ))}
    </Container>
  );
};

export default ScenarioSelection;
